import { useEffect, useMemo, useState } from "react";
import {
  VEHICLE_SPEC_FIELDS,
  normalizeVehicleSpecs
} from "../utils/buildStages";

const cylinderOptions = [
  "",
  "Inline-3",
  "Inline-4",
  "Inline-5",
  "Inline-6",
  "Flat-4",
  "Flat-6",
  "V6",
  "V8",
  "V10",
  "V12",
  "Rotary",
  "Electric",
  "Other"
];

const aspirationOptions = [
  "",
  "Naturally Aspirated",
  "Turbocharged",
  "Twin-Turbocharged",
  "Supercharged",
  "Twincharged",
  "Electric",
  "Other"
];

const drivetrainOptions = ["", "FWD", "RWD", "AWD", "4WD", "Other"];

const fuelTypeOptions = [
  "",
  "Gasoline",
  "Diesel",
  "E85",
  "Flex Fuel",
  "Hybrid",
  "Electric",
  "Other"
];

const fieldConfig = {
  engine: {
    type: "text",
    placeholder: "Toyota 2ZZ-GE"
  },
  displacement: {
    type: "text",
    placeholder: "1.8L"
  },
  cylinders: {
    type: "select",
    options: cylinderOptions
  },
  aspiration: {
    type: "select",
    options: aspirationOptions
  },
  power: {
    type: "text",
    placeholder: "215 WHP"
  },
  torque: { type: "text", placeholder: "150 lb-ft (include unit and measurement basis)" },
  transmission: {
    type: "text",
    placeholder: "6-Speed Manual"
  },
  drivetrain: {
    type: "select",
    options: drivetrainOptions
  },
  weight: {
    type: "text",
    placeholder: "1,980 lbs"
  },
  fuelType: {
    type: "select",
    options: fuelTypeOptions
  }
};

function VehicleSpecsModal({
  isOpen,
  onClose,
  oemSpecs,
  setOemSpecs,
  phaseSpecs,
  setPhaseSpecs,
  currentPhaseName
}) {
  const [oemDraft, setOemDraft] = useState(normalizeVehicleSpecs());
  const [phaseDraft, setPhaseDraft] = useState(normalizeVehicleSpecs());
  const [confirmCopyOpen, setConfirmCopyOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setOemDraft(normalizeVehicleSpecs(oemSpecs));
    setPhaseDraft(normalizeVehicleSpecs(phaseSpecs));
    setConfirmCopyOpen(false);
  }, [isOpen, oemSpecs, phaseSpecs]);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const hasPhaseDraftValues = useMemo(() => {
    const normalizedDraft = normalizeVehicleSpecs(phaseDraft);

    return VEHICLE_SPEC_FIELDS.some(({ key }) => normalizedDraft[key]);
  }, [phaseDraft]);

  if (!isOpen) return null;

  const updateDraftValue = (draftSetter, key, value) => {
    draftSetter((current) => ({
      ...current,
      [key]: value
    }));
  };

  const applySpecs = () => {
    setOemSpecs(normalizeVehicleSpecs(oemDraft));
    setPhaseSpecs(normalizeVehicleSpecs(phaseDraft));
    onClose();
  };

  const copyOemSpecs = () => {
    if (hasPhaseDraftValues && !confirmCopyOpen) {
      setConfirmCopyOpen(true);
      return;
    }

    setPhaseDraft(normalizeVehicleSpecs(oemDraft));
    setConfirmCopyOpen(false);
  };

  const renderSpecFields = (draft, draftSetter, prefix) => (
    <div style={fieldsGridStyle}>
      {VEHICLE_SPEC_FIELDS.map(({ key, label }) => {
        const config = fieldConfig[key];
        const value = draft[key] || "";

        if (config.type === "select") {
          const options = getSelectOptions(config.options, value);

          return (
            <label key={key} style={fieldStyle}>
              <span style={labelStyle}>{label}</span>

              <select
                value={value}
                onChange={(event) =>
                  updateDraftValue(draftSetter, key, event.target.value)
                }
                style={inputStyle}
              >
                {options.map((option) => (
                  <option key={`${prefix}-${key}-${option}`} value={option}>
                    {option || "Select"}
                  </option>
                ))}
              </select>
            </label>
          );
        }

        return (
          <label key={key} style={fieldStyle}>
            <span style={labelStyle}>{label}</span>

            <input
              value={value}
              placeholder={config.placeholder}
              onChange={(event) =>
                updateDraftValue(draftSetter, key, event.target.value)
              }
              style={inputStyle}
            />
          </label>
        );
      })}
    </div>
  );

  return (
    <div style={overlayStyle} onMouseDown={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="vehicle-specs-title"
        style={modalStyle}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={headerStyle}>
          <div>
            <h2 id="vehicle-specs-title" style={titleStyle}>
              Vehicle Specs
            </h2>
            <p style={subtitleStyle}>
              Compare the factory configuration with the current Build Phase.
            </p>
          </div>

          <button type="button" onClick={onClose} style={closeButtonStyle}>
            ×
          </button>
        </div>

        <div style={contentStyle}>
          <section style={specSectionStyle}>
            <div style={sectionHeaderStyle}>
              <h3 style={sectionTitleStyle}>OEM</h3>
            </div>

            {renderSpecFields(oemDraft, setOemDraft, "oem")}
          </section>

          <section style={specSectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h3 style={sectionTitleStyle}>
                  Current Build Phase
                  {currentPhaseName ? ` — ${currentPhaseName}` : ""}
                </h3>
              </div>

              <button
                type="button"
                onClick={copyOemSpecs}
                style={copyButtonStyle}
              >
                {confirmCopyOpen ? "Confirm Replace" : "Copy OEM Specs"}
              </button>
            </div>

            {confirmCopyOpen && (
              <div style={confirmCopyStyle}>
                This will replace the current Build Phase specs with the OEM
                values. Click Confirm Replace to continue.
              </div>
            )}

            {renderSpecFields(phaseDraft, setPhaseDraft, "phase")}
          </section>
        </div>

        <div style={footerStyle}>
          <button type="button" onClick={onClose} style={cancelButtonStyle}>
            Cancel
          </button>

          <button type="button" onClick={applySpecs} style={applyButtonStyle}>
            Apply Specs
          </button>
        </div>
      </div>
    </div>
  );
}

const getSelectOptions = (options, currentValue) => {
  if (!currentValue || options.includes(currentValue)) {
    return options;
  }

  return [...options, currentValue];
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.82)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 30000,
  padding: "20px",
  boxSizing: "border-box"
};

const modalStyle = {
  backgroundColor: "#181818",
  border: "1px solid #333",
  borderRadius: "20px",
  maxWidth: "760px",
  width: "100%",
  maxHeight: "90vh",
  color: "white",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: "0 24px 70px rgba(0,0,0,0.5)"
};

const headerStyle = {
  padding: "20px",
  borderBottom: "1px solid #2a2a2a",
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "flex-start"
};

const titleStyle = {
  margin: 0,
  fontSize: "24px"
};

const subtitleStyle = {
  margin: "8px 0 0",
  color: "#aaa",
  lineHeight: "1.5"
};

const closeButtonStyle = {
  backgroundColor: "#252525",
  color: "white",
  border: "1px solid #444",
  borderRadius: "999px",
  width: "38px",
  height: "38px",
  cursor: "pointer",
  fontSize: "24px",
  lineHeight: "1"
};

const contentStyle = {
  padding: "18px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "16px"
};

const specSectionStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #303030",
  borderRadius: "16px",
  padding: "16px",
  boxSizing: "border-box"
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  marginBottom: "14px",
  flexWrap: "wrap"
};

const sectionTitleStyle = {
  margin: 0,
  color: "#e10600",
  fontSize: "16px"
};

const fieldsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
  gap: "12px"
};

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "6px"
};

const labelStyle = {
  color: "#ddd",
  fontSize: "13px",
  fontWeight: "700"
};

const inputStyle = {
  width: "100%",
  padding: "13px",
  borderRadius: "12px",
  border: "1px solid #333",
  backgroundColor: "#141414",
  color: "white",
  fontSize: "16px",
  boxSizing: "border-box"
};

const copyButtonStyle = {
  backgroundColor: "#333",
  color: "white",
  border: "1px solid #444",
  borderRadius: "999px",
  padding: "9px 12px",
  fontWeight: "bold",
  cursor: "pointer"
};

const confirmCopyStyle = {
  backgroundColor: "#241616",
  border: "1px solid rgba(225,6,0,0.45)",
  color: "#f0c2c2",
  borderRadius: "12px",
  padding: "10px",
  fontSize: "13px",
  lineHeight: "1.45",
  marginBottom: "12px"
};

const footerStyle = {
  padding: "16px 18px",
  borderTop: "1px solid #2a2a2a",
  display: "flex",
  gap: "10px"
};

const cancelButtonStyle = {
  flex: 1,
  backgroundColor: "#333",
  color: "white",
  border: "none",
  borderRadius: "12px",
  padding: "14px",
  fontWeight: "bold",
  cursor: "pointer"
};

const applyButtonStyle = {
  ...cancelButtonStyle,
  backgroundColor: "#e10600"
};

export default VehicleSpecsModal;
